import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteCard from "@/components/feature/SiteCard";
import SiteLogo from "@/components/feature/SiteLogo";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  formatVisits,
  getCategoryById,
  getHost,
  getRelatedSites,
  getSiteById,
} from "@/utils/site";

export default function Detail() {
  const { siteId = "" } = useParams();
  const site = getSiteById(siteId);
  const category = site ? getCategoryById(site.categoryId) : undefined;
  const [toast, setToast] = useState("");

  usePageMeta({
    title: site ? `${site.name} - 网址导航` : "网站信息暂不可用 - 网址导航",
    description: site
      ? `${site.desc}${site.detail}`.slice(0, 150)
      : "该网站信息暂不可用，请返回首页浏览其他热门网站。",
    keywords: site ? `${site.name},${site.tags.join(",")},网址导航,网站入口` : "网址导航",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleCopy = async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        showToast("链接已复制，可粘贴分享给好友");
      } else {
        showToast("当前浏览器不支持自动复制，请手动复制地址栏链接");
      }
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!site) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-error-warning-line"
          title="该网站信息暂不可用"
          description="你要查看的网站不存在或数据已丢失，返回首页继续浏览其他热门网站吧。"
          action={{ label: "返回首页", to: "/" }}
        />
      </div>
    );
  }

  const related = getRelatedSites(site, 6);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        {category ? (
          <>
            <Link to={`/category/${category.id}`} className="transition-colors hover:text-primary-600">
              {category.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
          </>
        ) : null}
        <span className="font-medium text-foreground-800">{site.name}</span>
      </nav>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <SiteLogo site={site} size={72} />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{site.name}</h1>
                {site.featured ? (
                  <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-700">
                    精选推荐
                  </span>
                ) : null}
                {site.isNew ? (
                  <span className="rounded-full bg-accent-500 px-2.5 py-0.5 text-xs font-medium text-background-50">
                    新收录
                  </span>
                ) : null}
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-link"></i>
                {getHost(site.url)}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={site.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-external-link-line"></i>
              访问网站
            </a>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-share-line"></i>
              分享链接
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-500"></i>
              网站简介
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">{site.desc}</p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">{site.detail}</p>

            <h3 className="mt-6 flex items-center gap-2 text-base font-semibold text-foreground-950">
              <i className="ri-checkbox-circle-line text-accent-500"></i>
              适用场景
            </h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground-700">
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-primary-500"></i>
                {category ? `需要在「${category.name}」场景下快速找到可靠入口时` : "需要快速找到常用网站入口时"}
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-primary-500"></i>
                关键词标签：{site.tags.join("、")}
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line mt-0.5 text-primary-500"></i>
                适合办公、学习与日常上网时快速访问，点击按钮即可直达官网
              </li>
            </ul>
          </div>

          {related.length > 0 ? (
            <div className="mt-6">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                <i className="ri-links-line text-primary-500"></i>
                相关网站推荐
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {related.map((item) => (
                  <SiteCard key={item.id} site={item} />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-semibold text-foreground-950">所属分类</h2>
            {category ? (
              <Link
                to={`/category/${category.id}`}
                className="mt-3 flex items-center gap-3 rounded-md border border-background-200 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-lg`}></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{category.name}导航</p>
                  <p className="text-xs text-foreground-500">点击进入该分类</p>
                </div>
                <i className="ri-arrow-right-s-line ml-auto text-foreground-400"></i>
              </Link>
            ) : null}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-semibold text-foreground-950">分类标签</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {site.tags.map((t) => (
                <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-700">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-base font-semibold text-foreground-950">网站数据</h2>
            <dl className="mt-3 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="flex items-center gap-1.5 text-foreground-600">
                  <i className="ri-eye-line"></i>访问量
                </dt>
                <dd className="font-medium text-foreground-900">{formatVisits(site.visits)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="flex items-center gap-1.5 text-foreground-600">
                  <i className="ri-calendar-line"></i>更新时间
                </dt>
                <dd className="font-medium text-foreground-900">{site.updatedAt}</dd>
              </div>
            </dl>
          </div>

          <p className="rounded-lg bg-secondary-100 p-4 text-xs leading-relaxed text-secondary-800">
            <i className="ri-information-line mr-1"></i>
            分享功能为前端模拟，仅复制当前页面链接；本站不提供收藏功能。
          </p>
        </aside>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      ) : null}
    </div>
  );
}